import React, { useState, useEffect, useCallback } from 'react';
import { a2aStaffService } from '../../a2a/services/a2aStaffService.js';
import './StaffPage.css';

const StaffPage = ({ onBack }) => {
  const [staffData, setStaffData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Generate fallback staff data
  const getFallbackStaffData = () => {
    const fallbackStaff = [
      { id: 'ST001', name: '陳美玲', department: '教學支援', position: '實驗室技術員', email: 'meiling.chen@myclass.edu.hk', phone: '2234-5678' },
      { id: 'ST002', name: '李志強', department: '教學支援', position: '圖書館助理', email: 'chikeung.li@myclass.edu.hk', phone: '2234-5679' },
      { id: 'ST003', name: '黃小華', department: '教學支援', position: '多媒體技術員', email: 'siuwah.wong@myclass.edu.hk', phone: '2234-5680' },
      { id: 'ST004', name: '張偉明', department: '技術支援', position: '電腦技術員', email: 'waiming.cheung@myclass.edu.hk', phone: '2234-5681' },
      { id: 'ST005', name: '林淑芬', department: '技術支援', position: '網絡管理員', email: 'shukfan.lam@myclass.edu.hk', phone: '2234-5682' },
      { id: 'ST006', name: '劉家豪', department: '學生輔導', position: '學生輔導員', email: 'kaho.lau@myclass.edu.hk', phone: '2234-5683' },
      { id: 'ST007', name: '吳詠詩', department: '學生輔導', position: '心理輔導師', email: 'wingsze.ng@myclass.edu.hk', phone: '2234-5684' },
      { id: 'ST008', name: '馬建華', department: '行政部門', position: '行政助理', email: 'kinwah.ma@myclass.edu.hk', phone: '2234-5685' },
      { id: 'ST009', name: '鄭麗華', department: '行政部門', position: '會計文員', email: 'laiwah.cheng@myclass.edu.hk', phone: '2234-5686' },
      { id: 'ST010', name: '梁志偉', department: '行政部門', position: '人事助理', email: 'chiwai.leung@myclass.edu.hk', phone: '2234-5687' },
      { id: 'ST011', name: '何國強', department: '設施管理', position: '設施管理員', email: 'kwokkeung.ho@myclass.edu.hk', phone: '2234-5688' },
      { id: 'ST012', name: '譚美鳳', department: '設施管理', position: '清潔主管', email: 'meifung.tam@myclass.edu.hk', phone: '2234-5689' }
    ];

    // Process fallback data to match expected structure
    const totalStaff = fallbackStaff.length;
    const departmentGroups = {};
    
    fallbackStaff.forEach(staff => {
      const dept = staff.department;
      if (!departmentGroups[dept]) {
        departmentGroups[dept] = [];
      }
      departmentGroups[dept].push(staff);
    });

    const totalDepartments = Object.keys(departmentGroups).length;
    const totalPositions = totalStaff;

    // Create department statistics with colors
    const departmentStats = {};
    const colors = ['#3182ce', '#38a169', '#d69e2e', '#e53e3e', '#805ad5'];
    let colorIndex = 0;
    
    Object.entries(departmentGroups).forEach(([dept, staffList]) => {
      departmentStats[dept] = {
        name: dept,
        count: staffList.length,
        color: colors[colorIndex % colors.length],
        staff: staffList
      };
      colorIndex++;
    });

    // Create staffByCategory structure
    const teachingStaff = fallbackStaff.filter(staff => staff.department === '教學支援');
    const staffByCategory = {
      teachingAssistants: teachingStaff.filter(staff => staff.position.includes('助理')),
      labTechnicians: teachingStaff.filter(staff => staff.position.includes('技術員')),
      computerTechnicians: fallbackStaff.filter(staff => staff.department === '技術支援'),
      supportStaff: fallbackStaff.filter(staff => staff.department === '學生輔導'),
      administrativeStaff: fallbackStaff.filter(staff => staff.department === '行政部門'),
      securityStaff: [], // No security staff in fallback data
      schoolLabourers: fallbackStaff.filter(staff => staff.department === '設施管理')
    };

    return {
      totalStaff,
      totalDepartments,
      totalPositions,
      departmentStats,
      staff: fallbackStaff,
      recentStaff: fallbackStaff.slice(0, 5), // Show first 5 as "recent"
      staffByCategory
    };
  };

  // Fetch staff data using A2A service
  const fetchStaffData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[StaffPage] Starting to fetch staff data via A2A...');
      
      // Fetch staff data using A2A service
      const response = await a2aStaffService.getStaffData('tn_01');
      
      console.log('[StaffPage] A2A response:', { success: response.success, hasData: !!response.data });
      
      // If there's an error or no data, use fallback data
      if (!response.success || !response.data || response.data.totalStaff === 0) {
        console.warn('[StaffPage] Using fallback staff data due to:', response.error || 'No data from database');
        const fallbackData = getFallbackStaffData();
        setStaffData(fallbackData);
        console.log('[StaffPage] Fallback staff data loaded successfully');
        return;
      }
      
      console.log('[StaffPage] Raw staff data received:', response.data);
      console.log('[StaffPage] Total staff:', response.data.totalStaff);
      console.log('[StaffPage] Total departments:', response.data.totalDepartments);
      
      setStaffData(response.data);
      console.log('[StaffPage] Staff data loaded successfully via A2A');
      
    } catch (err) {
      console.error('[StaffPage] Unexpected error in fetchStaffData:', err);
      console.warn('[StaffPage] Using fallback staff data due to error');
      const fallbackData = getFallbackStaffData();
      setStaffData(fallbackData);
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper functions for staff data
  const getStaffAvatar = (staff) => {
    const name = staff.name || 'Unknown'
    return name.charAt(0)
  }

  const getStaffDepartment = (staff) => {
    return staff.department || '未知部門'
  }

  const getStaffPosition = (staff) => {
    return staff.position || '未知職位'
  }

  useEffect(() => {
    // Fetch staff data on component mount
    fetchStaffData();
  }, [fetchStaffData]);

  if (loading) {
    return (
      <div className="staff-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>職員管理</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>正在載入職員資料...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="staff-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>職員管理</h1>
        </div>
        <div className="error-container">
          <p>載入失敗: {error}</p>
          <button onClick={fetchStaffData} className="retry-button">
            重試
          </button>
        </div>
      </div>
    );
  }

  if (!staffData) {
    return (
      <div className="staff-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>職員管理</h1>
        </div>
        <div className="no-data-container">
          <p>暫無職員資料</p>
        </div>
      </div>
    );
  }

  return (
    <div className="staff-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-button" onClick={onBack}>
          ← 返回主頁
        </button>
        <h1>職員管理</h1>
        <button className="refresh-button" onClick={fetchStaffData} title="重新載入">
          🔄
        </button>
      </div>
      
      {/* Debug info */}
      <div style={{ background: '#f0f0f0', padding: '10px', margin: '10px 0', borderRadius: '5px', fontSize: '12px' }}>
        <strong>Debug Info:</strong> Loading: {loading ? 'Yes' : 'No'}, Error: {error || 'None'}, Data: {staffData ? 'Loaded' : 'None'}
        <br />
        {staffData && (
          <>
            <strong>Data Details:</strong> Total Staff: {staffData.totalStaff}, Departments: {staffData.totalDepartments}
            <br />
            <strong>Active Tab:</strong> {activeTab}
            <br />
            <strong>Staff Categories:</strong> {staffData.staffByCategory ? Object.keys(staffData.staffByCategory).map(key => `${key}: ${staffData.staffByCategory[key]?.length || 0}`).join(', ') : 'None'}
          </>
        )}
        <br />
        <button onClick={() => {
          console.log('Manual test button clicked');
          console.log('Current staffData:', staffData);
          fetchStaffData();
        }} style={{ marginTop: '5px', padding: '5px 10px' }}>
          Test Data Fetch
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="nav-tabs">
        <button 
          className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          總覽
        </button>
        <button 
          className={`nav-tab ${activeTab === 'teaching' ? 'active' : ''}`}
          onClick={() => setActiveTab('teaching')}
        >
          教學支援
        </button>
        <button 
          className={`nav-tab ${activeTab === 'technical' ? 'active' : ''}`}
          onClick={() => setActiveTab('technical')}
        >
          技術支援
        </button>
        <button 
          className={`nav-tab ${activeTab === 'support' ? 'active' : ''}`}
          onClick={() => setActiveTab('support')}
        >
          學生輔導
        </button>
        <button 
          className={`nav-tab ${activeTab === 'admin' ? 'active' : ''}`}
          onClick={() => setActiveTab('admin')}
        >
          行政部門
        </button>
        <button 
          className={`nav-tab ${activeTab === 'facilities' ? 'active' : ''}`}
          onClick={() => setActiveTab('facilities')}
        >
          設施管理
        </button>
      </div>

      {/* Content */}
      <div className="page-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            {/* Summary Cards */}
            <div className="summary-section">
              <div className="summary-card">
                <div className="card-icon">👥</div>
                <div className="card-content">
                  <h3>總職員數</h3>
                  <div className="card-number">{staffData.totalStaff}</div>
                  <p>全校職員</p>
                </div>
              </div>
              
              <div className="summary-card">
                <div className="card-icon">🏢</div>
                <div className="card-content">
                  <h3>部門數量</h3>
                  <div className="card-number">{staffData.totalDepartments}</div>
                  <p>專業部門</p>
                </div>
              </div>
              
              <div className="summary-card">
                <div className="card-icon">📋</div>
                <div className="card-content">
                  <h3>職位種類</h3>
                  <div className="card-number">{staffData.totalPositions}</div>
                  <p>專業職位</p>
                </div>
              </div>
            </div>

            {/* Department Overview */}
            <div className="department-overview">
              <h2>部門概覽</h2>
              <div className="department-grid">
                {Object.values(staffData.departmentStats).map((dept, index) => (
                  <div key={index} className="department-card" style={{ borderLeftColor: dept.color }}>
                    <div className="department-header">
                      <h3>{dept.name}</h3>
                      <span className="staff-count">{dept.count}人</span>
                    </div>
                    <div className="department-staff">
                      {dept.staff.slice(0, 3).map((staff, staffIndex) => (
                        <div key={staffIndex} className="staff-preview">
                          <span className="staff-name">{staff.name}</span>
                          <span className="staff-position">{staff.position}</span>
                        </div>
                      ))}
                      {dept.count > 3 && (
                        <div className="more-staff">+{dept.count - 3} 更多...</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Staff */}
            <div className="recent-staff-section">
              <h2>最近職員</h2>
              <div className="staff-list">
                {staffData.recentStaff.map((staff, index) => (
                  <div key={staff.id || index} className="staff-item">
                    <div className="staff-avatar">
                      {getStaffAvatar(staff)}
                    </div>
                    <div className="staff-info">
                      <h4>{staff.name}</h4>
                      <p>{getStaffDepartment(staff)}</p>
                      <span className="staff-position">{getStaffPosition(staff)}</span>
                    </div>
                    <div className="staff-status">在職</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'teaching' && (
          <div className="teaching-section">
            <h2>教學支援部</h2>
            <div className="staff-category-grid">
              {(staffData.staffByCategory?.teachingAssistants || []).map((staff, index) => (
                <div key={staff.id || index} className="staff-card">
                  <div className="staff-avatar-large">
                    {getStaffAvatar(staff)}
                  </div>
                  <div className="staff-details">
                    <h3>{staff.name}</h3>
                    <p className="english-name">{staff.english_name || staff.englishName}</p>
                    <p className="department">{staff.department}</p>
                    <p className="position">{staff.position}</p>
                    {staff.specialty && <p className="specialty">{staff.specialty}</p>}
                    <div className="contact-info">
                      {staff.phone && <span>📞 {staff.phone}</span>}
                      {staff.email && <span>✉️ {staff.email}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'technical' && (
          <div className="technical-section">
            <h2>技術支援部</h2>
            
            <div className="tech-category">
              <h3>實驗室技術部</h3>
              <div className="staff-category-grid">
                {(staffData.staffByCategory?.labTechnicians || []).map((staff, index) => (
                  <div key={staff.id || index} className="staff-card">
                    <div className="staff-avatar-large">
                      {getStaffAvatar(staff)}
                    </div>
                    <div className="staff-details">
                      <h3>{staff.name}</h3>
                      <p className="english-name">{staff.english_name || staff.englishName}</p>
                      <p className="department">{staff.department}</p>
                      <p className="position">{staff.position}</p>
                      {staff.specialty && <p className="specialty">{staff.specialty}</p>}
                      <div className="contact-info">
                        <span>📞 {staff.phone}</span>
                        <span>✉️ {staff.email}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="tech-category">
              <h3>資訊科技部</h3>
              <div className="staff-category-grid">
                {(staffData.staffByCategory?.computerTechnicians || []).map((staff, index) => (
                  <div key={staff.id || index} className="staff-card">
                    <div className="staff-avatar-large">
                      {getStaffAvatar(staff)}
                    </div>
                    <div className="staff-details">
                      <h3>{staff.name}</h3>
                      <p className="english-name">{staff.english_name || staff.englishName}</p>
                      <p className="department">{staff.department}</p>
                      <p className="position">{staff.position}</p>
                      {staff.specialty && <p className="specialty">{staff.specialty}</p>}
                      <div className="contact-info">
                        <span>📞 {staff.phone}</span>
                        <span>✉️ {staff.email}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'support' && (
          <div className="support-section">
            <h2>學生輔導部</h2>
            <div className="staff-category-grid">
              {(staffData.staffByCategory?.supportStaff || []).map((staff, index) => (
                <div key={staff.id || index} className="staff-card">
                  <div className="staff-avatar-large">
                    {getStaffAvatar(staff)}
                  </div>
                  <div className="staff-details">
                    <h3>{staff.name}</h3>
                    <p className="english-name">{staff.english_name || staff.englishName}</p>
                    <p className="department">{staff.department}</p>
                    <p className="position">{staff.position}</p>
                    {staff.specialty && <p className="specialty">{staff.specialty}</p>}
                    <div className="contact-info">
                      {staff.phone && <span>📞 {staff.phone}</span>}
                      {staff.email && <span>✉️ {staff.email}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'admin' && (
          <div className="admin-section">
            <h2>行政部門</h2>
            <div className="staff-category-grid">
              {(staffData.staffByCategory?.administrativeStaff || []).map((staff, index) => (
                <div key={staff.id || index} className="staff-card">
                  <div className="staff-avatar-large">
                    {getStaffAvatar(staff)}
                  </div>
                  <div className="staff-details">
                    <h3>{staff.name}</h3>
                    <p className="english-name">{staff.english_name || staff.englishName}</p>
                    <p className="department">{staff.department}</p>
                    <p className="position">{staff.position}</p>
                    {staff.specialty && <p className="specialty">{staff.specialty}</p>}
                    <div className="contact-info">
                      {staff.phone && <span>📞 {staff.phone}</span>}
                      {staff.email && <span>✉️ {staff.email}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'facilities' && (
          <div className="facilities-section">
            <h2>設施管理部</h2>
            
            {/* Debug info for facilities */}
            <div style={{ background: '#ffe6e6', padding: '10px', margin: '10px 0', borderRadius: '5px', fontSize: '12px' }}>
              <strong>Facilities Debug:</strong> School Labourers: {staffData.staffByCategory?.schoolLabourers?.length || 0}, 
              Security Staff: {staffData.staffByCategory?.securityStaff?.length || 0}
              <br />
              <strong>All Categories:</strong> {staffData.staffByCategory ? JSON.stringify(Object.keys(staffData.staffByCategory)) : 'None'}
            </div>
            
            <div className="facilities-category">
              <h3>校工團隊</h3>
              <div className="staff-category-grid">
                {staffData.staffByCategory?.schoolLabourers?.length > 0 ? 
                  (staffData.staffByCategory?.schoolLabourers || []).map((staff, index) => (
                  <div key={staff.id || index} className="staff-card">
                    <div className="staff-avatar-large">
                      {getStaffAvatar(staff)}
                    </div>
                    <div className="staff-details">
                      <h3>{staff.name}</h3>
                      <p className="english-name">{staff.english_name || staff.englishName}</p>
                      <p className="department">{staff.department}</p>
                      <p className="position">{staff.position}</p>
                      {staff.specialty && <p className="specialty">{staff.specialty}</p>}
                      <div className="contact-info">
                        {staff.phone && <span>📞 {staff.phone}</span>}
                        {staff.email && <span>✉️ {staff.email}</span>}
                      </div>
                    </div>
                  </div>
                )) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    沒有找到校工資料
                  </div>
                )}
              </div>
            </div>

            <div className="facilities-category">
              <h3>保安部</h3>
              <div className="staff-category-grid">
                {staffData.staffByCategory?.securityStaff?.length > 0 ? 
                  (staffData.staffByCategory?.securityStaff || []).map((staff, index) => (
                    <div key={staff.id || index} className="staff-card">
                      <div className="staff-avatar-large">
                        {getStaffAvatar(staff)}
                      </div>
                      <div className="staff-details">
                        <h3>{staff.name}</h3>
                        <p className="english-name">{staff.english_name || staff.englishName}</p>
                        <p className="department">{staff.department}</p>
                        <p className="position">{staff.position}</p>
                        {staff.specialty && <p className="specialty">{staff.specialty}</p>}
                        <div className="contact-info">
                          {staff.phone && <span>📞 {staff.phone}</span>}
                          {staff.email && <span>✉️ {staff.email}</span>}
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                      沒有找到保安資料
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffPage;

